import React, { FunctionComponent } from 'react'
import DropdownMenu from '../../components/dropdown/DropdownMenu'
import SvgFragment from '../SvgFragment'
import DropdownItem from '../../components/dropdown/DropdownItem'
import Row from '../../components/Row'
import styled from 'styled-components'
import TooltipWrapper from '../../components/TooltipWrapper'

interface ICustomAction {
    icon: string,
    title: string,
    onClickHandler?: (e: any) => void,
    enabled?: boolean,
    visible?: boolean,
    destructive?: boolean
}

interface IActionItem {
    icon: string,
    title: string,
    slug: string,
    enabled: boolean,
    visible: boolean,
    destructive: boolean,
    onClickHandler?: () => void
}

interface IEditDeleteActionDropdownProps {
    title?: string,
    deleteTitle?: string,
    editEnabled?: boolean,
    deleteEnabled?: boolean,
    type: string,
    object: Object,
    className?: string,
    onEditHandler?: (e: any) => void
    onDeleteHandler?: (e: any) => void
    disabled?: boolean
    tooltipMessage?: string
    deleteTooltipMessage?: string
    customActions?: ICustomAction[]
}

const EditDeleteActionDropdown: FunctionComponent<IEditDeleteActionDropdownProps> = ({
    title = '',
    deleteTitle = '',
    className,
    type,
    object,
    editEnabled = true,
    onEditHandler,
    deleteEnabled = true,
    onDeleteHandler,
    disabled = false,
    tooltipMessage = 'Actions',
    deleteTooltipMessage,
    customActions = []
}) => {
    const editDeleteActions: IActionItem[] = [
        {
            icon: 'pen',
            title: `${title === '' ? `Edit ${type}` : title}`,
            slug: 'edit',
            enabled: editEnabled && !disabled,
            visible: true,
            destructive: false,
            onClickHandler: () => onEditHandler && onEditHandler(object)
        },
        ...customActions.map(action => ({
            icon: action.icon,
            title: action.title,
            slug: action.title.toLowerCase().replace(/\s+/g, '-'),
            enabled: (action.enabled !== false) && !disabled,
            visible: action.visible !== false,
            destructive: action.destructive || false,
            onClickHandler: () => action.onClickHandler && action.onClickHandler(object)
        })),
        {
            icon: 'trashCan',
            title: `${deleteTitle === '' ? `Delete ${type}` : deleteTitle}`,
            slug: 'delete',
            enabled: deleteEnabled && !disabled,
            destructive: true,
            visible: true,
            onClickHandler: () => onDeleteHandler && onDeleteHandler(object)
        }
    ]
    return <DropdownMenu className={className} SVGtype='threeDots' $hideToggle $transparent tooltipMessage={tooltipMessage} disabled={disabled}>
        {editDeleteActions.map((editDeleteAction, index) => {
            const isCustomAction = index > 0 && index < editDeleteActions.length - 1; // Custom actions are between Edit and Delete
            const isDeleteAction = editDeleteAction.slug === 'delete';
            const tooltipForItem = isDeleteAction && deleteTooltipMessage ? deleteTooltipMessage : undefined;
            
            const dropdownItem = editDeleteAction.enabled && editDeleteAction.visible ? 
                <DropdownItem 
                    $danger={editDeleteAction.destructive} 
                    $disabled={!editDeleteAction.enabled}
                    onClickHandler={() => editDeleteAction.onClickHandler && editDeleteAction.onClickHandler()}
                >
                    <Row alignItems gap="5px">
                        {isCustomAction ? (
                            <IconWrapper>
                                <SvgFragment type={editDeleteAction.icon} />
                            </IconWrapper>
                        ) : (
                            <SvgFragment type={editDeleteAction.icon} />
                        )}
                        {editDeleteAction.title}
                    </Row>
                </DropdownItem> : 
                (!editDeleteAction.enabled && editDeleteAction.visible && isDeleteAction) ? (
                    <DropdownItem 
                        $danger={editDeleteAction.destructive} 
                        $disabled={true}
                    >
                        <Row alignItems gap="5px">
                            <SvgFragment type={editDeleteAction.icon} />
                            {editDeleteAction.title}
                        </Row>
                    </DropdownItem>
                ) : null
            
            // Wrap with TooltipWrapper if tooltip is needed
            if (!dropdownItem) return null;
            
            if (tooltipForItem) {
                return (
                        <TooltipWrapper key={index} tooltipText={tooltipForItem}>
                            <div>
                                {dropdownItem}
                            </div>
                        </TooltipWrapper>
                )
            }
            
            return <React.Fragment key={index}>{dropdownItem}</React.Fragment>
        })}
    </DropdownMenu>
}

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        width: 18px;
        height: 18px;
    }
`

export default EditDeleteActionDropdown
